import { Injectable } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs/Observable';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { LocalCacheService } from '../../core/local-cache/local-cache.service';
import { LoggerService } from '../../core/logger/global-logger.service';
import * as moment from 'moment';
import { DeleteConfirmationComponent } from '../components/delete-confirmation/delete-confirmation.component';
import { SharedCallsService } from './shared-calls.service';
import { SubheaderHandlerService } from './subheader-handler.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()

export class TimeoutHandlerService {
    caseId;
    private lockId: string;
    private persistlockId: string;
    private persistCaseId: string;
    setMarkAsset: boolean;
    translatedText = [];
    // backend update interval
    private backendLockUpdateTimeInterval = 720000; // (12mins)

    // idle Intervals
    private timeoutActionInterval = 45; // (45secs)
    private timeoutInterval = 900; // (900 secs = 15mins)

    private backendLockTimerReference: any;
    private modalRefInactive: any;

    // idle event hanlders
    private onIdleStartHandler; // after 15 minutes
    private onIdleEndHandler;
    private onTimeoutHandler; // 15min = 45 secs

    lockStatus: Observable<any>;
    private lockStatusSubject = new Subject<any>();

    constructor(
        private router: Router,
        private modalService: NgbModal,
        private storage: LocalCacheService,
        public idle: Idle,
        private logger: LoggerService,
        private sharedService: SharedCallsService,
        private subheaderHandlerService: SubheaderHandlerService,
        private translate: TranslateService) {
        this.lockStatus = this.lockStatusSubject.asObservable();
        this.caseId = this.storage.fetchData('caseId');
        translate.get([
           'COMMON.WARNING',
           'TIME_OUT.AT_TIME_OUT',
           'TIME_OUT.CONTIUE_EDITING',
           'COMMON.CLOSE',
           'TIME_OUT.CASE_NOT_EXISTS',
           'TIME_OUT.UNAUTHORIZED_USER',
           'TIME_OUT.OTHER_CASE_LOCKED'
          ]).subscribe((translations: string) => {
            this.translatedText[0] = translations['COMMON.WARNING'];
            this.translatedText[1] = translations['TIME_OUT.AT_TIME_OUT'];
            this.translatedText[2] = translations['TIME_OUT.CONTIUE_EDITING'];
            this.translatedText[3] = translations['COMMON.CLOSE'];
            this.translatedText[4] = translations['TIME_OUT.CASE_NOT_EXISTS'];
            this.translatedText[5] = translations['TIME_OUT.UNAUTHORIZED_USER'];
            this.translatedText[6] = translations['TIME_OUT.OTHER_CASE_LOCKED'];
          });
    }

    // For components to subscribe for change in lock status
    public updateLockStatus(updatedLockStatus: boolean) {
        this.lockStatusSubject.next(updatedLockStatus);
    }

    // to reset the idle service
    public reset() {
        this.idle.watch();
        this.logger.error(
            { 'message': moment().format('YY-MM-DD HH:mm:ss') + '- Timer reset ' + this.caseId },
            false, true);
    }

    // to stop everything (idle service & pooling)
    public stop() {
        // clearing the pooling
        clearInterval(this.backendLockTimerReference);
        // un subscribing all handlers
        if (this.onIdleEndHandler !== undefined) {
            this.onIdleEndHandler.unsubscribe();
            this.onIdleStartHandler.unsubscribe();
            this.onTimeoutHandler.unsubscribe();
        }

        // stopping the Idle service
        this.idle.stop();

        if (this.modalRefInactive !== undefined) {
            this.modalRefInactive.activeModal.close();
        }

    }

    // creating lock in backend
    public createLock(caseId) {
        const lid = this.getLockIdFromStorage();
        const cid = this.getCaseIdFromStorage();
        if (caseId !== null && caseId !== undefined && cid && caseId === cid && lid) {
            this.caseId = caseId;
            // Lock already exists for this site
            // lets return the lock id back to caller to further process
            // start the timer again

            const response = new Observable(observer => {

                this.sharedService.updateLock(lid).subscribe((data: any) => {

                    // console.log('put');
                    if (data.status === 200) {
                        this.setTimer();
                        // tslint:disable-next-line: max-line-length
                        observer.next(data);
                    } else {
                        this.deleteLock();
                        this.clearLockFromStorage();
                        this.stop();
                        this.router.navigate(['/evaluator']);
                    }
                }, (error: any) => {
                    this.deleteLock();
                    this.clearLockFromStorage();
                    this.stop();
                    this.router.navigate(['/evaluator']);
                });
            });
            return response;
        } else {
            // Either lock doesn't exists or stored id doesn't match with case id sent
            // clear the old lock first before creating a new lock
            this.deleteLock();
            this.caseId = caseId;
            const response = new Observable(observer => {
                const userId = this.storage.fetchData('userId');
                this.sharedService.caseLock(caseId, userId).subscribe((data: any) => {
                    // console.log('post');
                    if (data.status === 200) {

                        this.lockId = data.result.id;
                        this.addLockToStorage();
                        this.updateLockStatus(true);
                        // start the timer now
                        this.setTimer();
                        observer.next(this.lockId);
                        // tslint:disable-next-line: max-line-length
                    } else {
                        this.router.navigate(['/evaluator']);
                    }
                }, (error: any) => {
                    this.router.navigate(['/evaluator']);
                });
            });
            return response;
        }
    }

    // removing the lock in backend
    public deleteLock() {

        if (this.modalRefInactive !== undefined) {
            this.modalRefInactive.activeModal.close();
        }
        const lockId = this.getLockIdFromStorage();
        const response = new Observable(observer => {
            this.sharedService.deleteLock(lockId).subscribe((data: any) => {
                if (data) {

                    this.updateLockStatus(false);
                    this.clearLockFromStorage();
                    this.stop();
                } else {

                    this.clearLockFromStorage();
                    this.stop();
                    this.logger.error({ message: 'An error occured in delete lock' }, false, true);
                }
            }, (error: any) => {
                this.clearLockFromStorage();
                this.stop();
                this.logger.error({ message: 'An error occured in delete lock' }, false, true);
            });
        });
        return response;
    }

    // #section - private
    private setTimer() {
        this.intiliazeTimer(this.idle);
        this.updateTimer();
    }

    // continue button clicked in the modal
    private continueClicked() {
        this.reset();
        if (this.modalRefInactive !== undefined) {
            this.modalRefInactive.activeModal.close();
        }
        const cid = this.getCaseIdFromStorage();
        this.createLock(cid).subscribe(lockDetails => {
        });
    }

    // close clicked in the modal
    public closeClicked() {
        // call subheader-service close method instead
        this.subheaderHandlerService.closeDanglingSessionClicked();
        if (this.modalRefInactive !== undefined) {
            this.modalRefInactive.activeModal.close();
        }
    }

    // init the service idle
    private intiliazeTimer(idle: Idle) {
         this.idle.clearInterrupts();
        // this.idle.setIdleName('assetTimeOut');
        this.idle.setIdle(this.timeoutInterval); // 15 min
        this.idle.setTimeout(this.timeoutActionInterval); // 45 seconds for action after idle
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES); // default actions of user that will reset the counter for idle
        this.onIdleEndHandler = this.idle.onIdleEnd.subscribe(() => {
            this.logger.error(
                { 'message': moment().format('YY-MM-DD HH:mm:ss') + '- No longer idle ' + this.caseId },
                false, true);
            this.reset();
        });

        this.onTimeoutHandler = this.idle.onTimeout.subscribe(() => {
            // this.idle.stop();
            const lockId = this.getLockIdFromStorage();
            this.sharedService.deleteLock(lockId).subscribe((data: any) => {
                if (data) {
                    this.updateLockStatus(false);
                    this.clearLockFromStorage();
                    this.stop();
                    if (this.modalRefInactive) {
                        this.modalRefInactive.activeModal.close();
                    }
                    this.router.navigate(['/evaluator']);
                } else {
                    this.clearLockFromStorage();
                    this.stop();
                    this.logger.error({ message: 'An error occured in delete lock' }, false, true);
                }
            });
        });
        this.onIdleStartHandler = this.idle.onIdleStart.subscribe(() => {
            if (this.modalRefInactive !== undefined) {
                this.modalRefInactive.activeModal.close();
            }
            this.logger.error(
                { 'message': moment().format('YY-MM-DD HH:mm:ss') + '- Idle state ' + this.caseId },
                false, true);

            this.modalRefInactive = this.modalService.open(DeleteConfirmationComponent, {
                backdrop: 'static',
                keyboard: false,
                windowClass: 'delete-modal timeout-modal'
            }).componentInstance;
            // tslint:disable-next-line: max-line-length
            this.modalRefInactive.modalText = this.translatedText[0];
            this.modalRefInactive.modContent = this.translatedText[1];
            this.modalRefInactive.isWarning = true;
            this.modalRefInactive.btnPrimary = { text: this.translatedText[2], isPrimary: true };
            this.modalRefInactive.btnSecondary = { text: this.translatedText[3], isPrimary: false };
            this.modalRefInactive.btnPrimaryClickEmitter.subscribe(($event) => {
                this.continueClicked();
            });
            this.modalRefInactive.btnSecondaryClickEmitter.subscribe(($event) => {
                this.closeClicked();
            });

        });
        this.reset();
    }

    // creating a async pooling
    private updateTimer() {
        this.backendLockTimerReference = setInterval(() => {
            const cid = this.getCaseIdFromStorage();
            this.createLock(cid);
        }, this.backendLockUpdateTimeInterval);
    }

    // managing storage
    private addLockToStorage() {
        if (this.getLockIdFromStorage() !== this.lockId) {
            this.persistlockId = this.lockId;
            this.persistCaseId = this.caseId;
            this.storage.storeData('lockId', this.lockId);
            this.storage.storeData('caseId', this.caseId);
        }
    }

    // To clear storage
    private clearLockFromStorage() {
        // console.log('clearLockFromStorage >', 'clearLockFromStorage');
        this.persistlockId = '';
        this.persistCaseId = '';
        this.storage.clearData('lockId');
        this.storage.clearData('caseId');
    }

    private getLockIdFromStorage(): string {
        this.persistlockId = this.storage.fetchData('lockId');
        this.persistCaseId = this.storage.fetchData('caseId');
        return this.persistlockId;
    }

    private getCaseIdFromStorage(): string {
        return this.persistCaseId;
    }

    getLockMessage(lockStatus, message): any {
        let errorMessage = '';
        let disableIcons: boolean;
        if (message === 'casenotexists') {
            errorMessage = this.translatedText[4];
            disableIcons = true;
        } else if (message === 'unauthorizeduser') {
            errorMessage = this.translatedText[5];
            disableIcons = true;
        } else if (message === 'someothercasealreadylockedbyyou') {
            errorMessage = this.translatedText[6];
            disableIcons = true;
        } else if (lockStatus && message !== 'lockedbysameuser') {
            errorMessage = message;
            disableIcons = false;
        }
        return {errorMessage: errorMessage, disableIcons: disableIcons};
    }

    isUserAuthorizedForSelectedCase(caseDetails): boolean {
        const loggedInUserId = this.storage.fetchData('userId');        
        const userRole = this.storage.fetchData('userRole');
        //Admin and marketer should have all permissions for any state of case
        if (userRole.toLowerCase() === 'admin' || userRole.toLowerCase() === 'marketer'){
            return true;
        } else {
            if (caseDetails.userId.toString() === loggedInUserId.toString()) {
                return true;
            } else {
                return false;
            }
        }            
      }
}



// WEBPACK FOOTER //
// D:/a/1/s/Fed/src/app/shared/services/timeout-handler.service.ts